import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProcessoContext, ProcessoUsuarioProvider } from '../../Routes/context/ProcessoUsuarioContext';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../utils/breadcrumbUtils';
import { StatusBanca } from '../../model/banca/Banca';
import MembroBanca, { MembroBancaUtils, StatusMembroNome } from '../../model/banca/MembroBanca';
import { BancaNovaResponse } from '../../model/banca/BancasNovaResponse';
import { FaUserCheck, FaUsers } from 'react-icons/fa';
import { BsPersonLinesFill } from 'react-icons/bs';
import { MembrosBancaViewModel } from '../../viewmodel/banca/MembrosBancaViewModel';
import { VinculoNome } from '../../model/Pessoa';
import { BancaViewModel } from '../../viewmodel/banca/BancaViewModel';
import ActionButton from './components/ActionButton';
import { getButtonConfigFromBackend } from './utils/bancaButtonConfigUtils';
import './index.css';

const ComposicaoBancaDetalhada: React.FC = () => {
  const { processoId } = useParams<{ processoId: string }>();

  if (!processoId) {
    return <div className="bancas-container">
      <div className="alert alert-danger">Erro: Processo ID não fornecido</div>
    </div>;
  }

  return (
    <ProcessoUsuarioProvider processoId={processoId}>
      <ComposicaoBancaDetalhadaContent />
    </ProcessoUsuarioProvider>
  );
};

const ComposicaoBancaDetalhadaContent: React.FC = () => {
  const { bancaId } = useParams<{ bancaId: string }>();
  const { processoId } = useParams<{ processoId: string }>();
  const { processo } = useProcessoContext();
  const { setBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  
  const [banca, setBanca] = useState<BancaNovaResponse | null>(null);
  const [membros, setMembros] = useState<MembroBanca[]>([]);
  const [loading, setLoading] = useState(true);
  const [riscadosMembros, setRiscadosMembros] = useState<Set<string>>(new Set());

  useEffect(() => {
    setBreadcrumbs(createBreadcrumbs.fromProcessos([
      { path: `/processos/${processoId}/bancas`, breadcrumb: 'Bancas' },
      { path: `/processos/${processoId}/bancas/${bancaId}/composicao`, breadcrumb: 'Composição Detalhada' },
    ]));
  }, [setBreadcrumbs, processoId, bancaId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!processoId || !bancaId) return;
      
      try {
        setLoading(true);
        const [bancasData, membrosData] = await Promise.all([
          BancaViewModel.getBancasNova(processoId),
          MembrosBancaViewModel.getMembrosCompletos(processoId, bancaId)
        ]);
        
        const bancaData = bancasData._embedded.bancasNovaResponseList.find(b => b.id === bancaId);
        if (!bancaData) {
          throw new Error('Banca não encontrada');
        }
        
        setBanca(bancaData);
        setMembros(membrosData);
      } catch (error) {
        console.error("Erro ao buscar dados da banca:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [processoId, bancaId]);

  const handleDisponibilidade = () => {
    navigate(`/processos/${processoId}/bancas/${bancaId}/disponibilidade-banca`, {
      state: { tipoBancaProps: banca?.tipoBanca.nome },
    });
  };

  const handleDefinirMembros = () => {
    navigate(`/processos/${processoId}/bancas/${bancaId}/definir-membros`);
  };

  const getStatusBadgeStyle = (status: StatusMembroNome) => {
    const styles = {
      [StatusMembroNome.CONSULTADO]: { backgroundColor: '#ffc107', color: '#212529' },
      [StatusMembroNome.CONFIRMADO]: { backgroundColor: '#0dcaf0', color: 'white' },
      [StatusMembroNome.DEFINIDO]: { backgroundColor: '#0d6efd', color: 'white' },
      [StatusMembroNome.CONVOCADO]: { backgroundColor: '#198754', color: 'white' },
      [StatusMembroNome.CONVOCACAO_CONFIRMADA]: { backgroundColor: '#198754', color: 'white' }
    };
    return styles[status] || { backgroundColor: '#6c757d', color: 'white' };
  };

  const toggleRiscado = (membroId: string) => {
    setRiscadosMembros(prev => {
      const newSet = new Set(prev);
      if (newSet.has(membroId)) {
        newSet.delete(membroId);
      } else {
        newSet.add(membroId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="bancas-container">
        <div style={{ textAlign: 'center', padding: '40px' }}>Carregando...</div>
      </div>
    );
  }

  if (!banca) {
    return (
      <div className="bancas-container">
        <div className="alert alert-danger">Banca não encontrada</div>
      </div>
    );
  }

  const isConsultarDisponibilidadeDisabled = banca.status !== StatusBanca.INDEFINIDA;
  const isDefinicaoMembrosDisabled = banca.status === StatusBanca.INDEFINIDA;
  const hasTodosMembrosConsultados = membros.length > 0 && membros.every(m => m.statusMembro !== StatusMembroNome.CONSULTADO);

  return (
    <div className="bancas-container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid #dee2e6'
      }}>
        <h2 style={{ margin: '0 0 5px 0', fontSize: '24px' }}>Composição de Banca</h2>
        <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', fontSize: '20px', color: '#6c757d' }}>
          <FaUsers />
          {banca.tipoBanca.nome} - {processo?.nome}
        </h1>
      </div>



      {/* Membros - Tabela */}
      <div>
        <h2 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
          Membros da Banca
        </h2>
        
        {membros.length === 0 ? (
          <div style={{ 
            color: '#6c757d', 
            fontSize: '14px', 
            textAlign: 'center',
            padding: '30px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px'
          }}>
            Não há membros definidos
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              backgroundColor: 'white',
              border: '1px solid #dee2e6'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '13px', fontWeight: '600' }}>Nome</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '13px', fontWeight: '600' }}>Papel</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '13px', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '13px', fontWeight: '600' }}>CPF</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '13px', fontWeight: '600' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '13px', fontWeight: '600' }}>Telefone</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '13px', fontWeight: '600' }}>Matrícula</th>
                </tr>
              </thead>
              <tbody>
                {MembroBancaUtils.ordenarMembros(membros).map((membro) => (
                  <tr 
                    key={membro.id}
                    onClick={() => toggleRiscado(membro.id!)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: riscadosMembros.has(membro.id!) ? '#f8f9fa' : 'white',
                      textDecoration: riscadosMembros.has(membro.id!) ? 'line-through' : 'none',
                      opacity: riscadosMembros.has(membro.id!) ? 0.7 : 1
                    }}
                  >
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', fontSize: '13px' }}>
                      {membro.pessoa.nome}
                      {membro.pessoa.vinculo.nome === VinculoNome.DOCENTE && (
                        <span style={{ color: '#6c757d', fontSize: '11px', marginLeft: '5px' }}>
                          ({membro.pessoa.vinculo.nome})
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', fontSize: '13px' }}>
                      {MembroBancaUtils.papelToAbreviacao(membro.papelMembro.nome)}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                      <span
                        style={{
                          ...getStatusBadgeStyle(membro.statusMembro),
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '500',
                          textTransform: 'uppercase'
                        }}
                      >
                        {membro.statusMembro}
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', fontSize: '13px' }}>
                      {membro.pessoa.cpf || '-'}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', fontSize: '13px' }}>
                      {membro.pessoa.email || '-'}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', fontSize: '13px' }}>
                      {membro.telefone || '-'}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', fontSize: '13px' }}>
                      {membro.pessoa.matricula || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <div style={{ 
              marginTop: '10px', 
              fontSize: '11px', 
              color: '#6c757d',
              fontStyle: 'italic'
            }}>
              Clique em uma linha para riscá-la (rascunho)
            </div>
          </div>
        )}
      </div>

      {/* Ações - Parte inferior */}
      <div style={{ 
        display: 'flex', 
        gap: '15px', 
        marginTop: '30px',
        paddingTop: '20px',
        borderTop: '1px solid #eee',
        justifyContent: 'center'
      }}>
        <ActionButton 
          config={getButtonConfigFromBackend(banca, 'consultarBanca')} 
          onClick={handleDisponibilidade}
          customIcon={<FaUserCheck style={{ fontSize: '12px' }} />}
          customTitle="Consultar Disponibilidade"
          showText={true}
          checkSize="12px"
          customStyle={{
            padding: '8px 16px',
            backgroundColor: 'transparent',
            color: '#007bff',
            border: '1px solid #007bff',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px'
          }}
        />

        <ActionButton 
          config={getButtonConfigFromBackend(banca, 'definirBanca')} 
          onClick={handleDefinirMembros}
          customIcon={<BsPersonLinesFill style={{ fontSize: '12px' }} />}
          customTitle="Definição de Membros"
          showText={true}
          checkSize="12px"
          customStyle={{
            padding: '8px 16px',
            backgroundColor: 'transparent',
            color: '#007bff',
            border: '1px solid #007bff',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px'
          }}
        />
      </div>
    </div>
  );
};

export default ComposicaoBancaDetalhada;
